import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { flushAnalytics } from '@bonusly/analytics';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useLocalStorage } from '../../lib/storage/hooks/use-local-storage';
import { getActiveSeasonConfig } from './seasonal-config';

interface SeasonalThemeContextValue {
	/** True if the user has the feature flag enabled (can see and use seasonal themes) */
	hasSeasonalThemePermission: boolean;
	/** True if user has permission AND has toggled the theme on */
	isSeasonalThemeEnabled: boolean;
	toggleTheme: () => void;
}

export const SeasonalThemeContext = createContext<SeasonalThemeContextValue | undefined>(undefined);

export const SeasonalThemeProvider: FC<PropsWithChildren> = ({ children }) => {
	const hasSeasonalThemesFlag = useFeatureFlag('seasonal-themes');
	const activeSeasonConfig = getActiveSeasonConfig();
	const [userPreference, setUserPreference] = useLocalStorage<boolean>(activeSeasonConfig.storageKey, false);

	const flagWasEverTrueRef = useRef(false);
	if (hasSeasonalThemesFlag) {
		flagWasEverTrueRef.current = true;
	}

	const isSeasonalThemeEnabled = Boolean(hasSeasonalThemesFlag && userPreference);

	// Clear stale preference when the feature flag is deliberately turned off.
	// The ref guard prevents clearing during initial load when the flag defaults
	// to false before the identity store has finished initializing.
	useEffect(() => {
		if (flagWasEverTrueRef.current && !hasSeasonalThemesFlag && userPreference) {
			setUserPreference(false);
		}
	}, [hasSeasonalThemesFlag, userPreference, setUserPreference]);

	const toggleTheme = useCallback(async () => {
		const newValue = !userPreference;
		// Write directly to localStorage before reload — useLocalStorage
		// persists via useEffect which wouldn't fire before the reload.
		localStorage.setItem(activeSeasonConfig.storageKey, JSON.stringify(newValue));
		// Flush queued Amplitude events (e.g. SeasonalTheme.Enabled/Disabled fired
		// by the caller) before tearing down the page — the reload would otherwise
		// cancel any in-flight tracking requests. Wrapped in try/finally so a
		// rejected flush (network error, adblocker, etc.) never prevents the
		// reload: analytics are best-effort, but the theme toggle must complete.
		try {
			await flushAnalytics();
		} catch {
			// Intentionally swallowed — losing a tracking event is an acceptable
			// tradeoff for guaranteeing the UI reaches a consistent state.
		}
		window.location.reload();
	}, [userPreference, activeSeasonConfig.storageKey]);

	const contextValue: SeasonalThemeContextValue = useMemo(
		() => ({
			hasSeasonalThemePermission: Boolean(hasSeasonalThemesFlag),
			isSeasonalThemeEnabled,
			toggleTheme,
		}),
		[hasSeasonalThemesFlag, isSeasonalThemeEnabled, toggleTheme]
	);

	return <SeasonalThemeContext.Provider value={contextValue}>{children}</SeasonalThemeContext.Provider>;
};

/**
 * Hook to access seasonal theme state and controls
 *
 * Returns safe defaults when used outside of SeasonalThemeProvider:
 * - hasSeasonalThemePermission: false
 * - isSeasonalThemeEnabled: false
 * - toggleTheme: no-op function
 *
 * This allows components to gracefully degrade in contexts without the provider.
 */
export const useSeasonalTheme = () => {
	const context = useContext(SeasonalThemeContext);

	if (!context) {
		return {
			hasSeasonalThemePermission: false,
			isSeasonalThemeEnabled: false,
			toggleTheme: () => {},
		};
	}

	return context;
};
